import { ErrorMessage } from '@hookform/error-message'
import {
  AutocompleteProps,
  Autocomplete,
  TextField,
  TextFieldProps,
  Typography,
  inputBaseClasses,
  formControlClasses,
  AutocompleteValue,
} from '@mui/material'
import {
  FieldErrors,
  FieldPath,
  FieldValues,
  UseControllerProps,
  Controller,
  PathValue,
} from 'react-hook-form'

type Props<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  TMuliple extends boolean | undefined = boolean,
  TFreeSolo extends boolean | undefined = boolean,
> = UseControllerProps<TFieldValues, TName> &
  Omit<TextFieldProps, 'onChange'> & { errors: FieldErrors<TFieldValues> } & Omit<
    AutocompleteProps<
      NonNullable<PathValue<TFieldValues, TName>> extends Array<string | number>
        ? NonNullable<PathValue<TFieldValues, TName>>[number]
        : PathValue<TFieldValues, TName>,
      TMuliple,
      undefined,
      TFreeSolo
    >,
    'renderInput' | 'onChange'
  > & {
    onChange?: (
      data: AutocompleteValue<
        NonNullable<PathValue<TFieldValues, TName>> extends Array<string | number>
          ? NonNullable<PathValue<TFieldValues, TName>>[number]
          : PathValue<TFieldValues, TName>,
        TMuliple,
        undefined,
        TFreeSolo
      >
    ) => void
  }

export const ControlledAutocomplete = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  TMuliple extends boolean | undefined = boolean,
  TFreeSolo extends boolean | undefined = boolean,
>({
  label,
  sx,
  placeholder,
  control,
  name,
  errors,
  variant,
  onChange,
  helperText,
  ...autocompleteProps
}: Props<TFieldValues, TName, TMuliple, TFreeSolo>) => {
  return (
    <Controller
      render={({ field }) => {
        return (
          <Autocomplete
            {...autocompleteProps}
            size={autocompleteProps.size ?? 'small'}
            value={
              field.value ||
              ((autocompleteProps.multiple ? [] : '') as PathValue<TFieldValues, TName>)
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={`${autocompleteProps.required ? '*' : ''}${label}`}
                placeholder={placeholder}
                variant={variant}
                sx={{ [`& .${inputBaseClasses.root}`]: { height: 1 } }}
                helperText={
                  <>
                    {helperText}
                    <ErrorMessage
                      errors={errors}
                      // @ts-expect-error no idea yet
                      name={name}
                      render={({ message }) => (
                        <Typography color="error" variant="body2">
                          {message}
                        </Typography>
                      )}
                    />
                  </>
                }
                slotProps={{ formHelperText: { component: 'div' } }}
              />
            )}
            onChange={(_, data) => {
              field.onChange?.(data)
              onChange?.(data)
            }}
            sx={{ height: 'fit-content', [`& .${formControlClasses.root}`]: { height: 1 }, ...sx }}
          />
        )
      }}
      control={control}
      name={name}
    />
  )
}
